import * as THREE from 'three';

/**
 * Soft radial-gradient sprites, drawn on a canvas so nothing is loaded.
 * alpha(t) = (1 - smoothstep(core, 1, t)) ^ power, white, with t = distance from the centre / radius.
 * A small core gives a bright pinpoint; a core of 0 and a high power gives a wide airy glow.
 */
export function makeDiscTexture(core: number, power: number, size = 128): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  const stops = 24;
  for (let i = 0; i <= stops; i++) {
    const t = i / stops;
    const s = Math.min(1, Math.max(0, (t - core) / (1 - core)));
    const a = Math.pow(1 - s * s * (3 - 2 * s), power);
    grad.addColorStop(t, `rgba(255,255,255,${a.toFixed(4)})`);
  }
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export interface SunSprites {
  /** wide, airy glow */
  soft: THREE.CanvasTexture;
  /** bright pinpoint with a small flat core */
  hard: THREE.CanvasTexture;
}

let cache: SunSprites | null = null;

/** Built on first use, shared by every object in the chapter, kept across restarts. */
export function getSprites(): SunSprites {
  if (!cache) cache = { soft: makeDiscTexture(0, 2.4), hard: makeDiscTexture(0.18, 1.6) };
  return cache;
}
